import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../components/ui/Toast';
import axiosInstance from '../../axiosConfig';
import { getPlatformDomainName } from '../../config/platformConfig';
import PayoutModal from '../../components/payout/PayoutModal';
import './ReferralDashboard.css';

// SVG Icons
const GiftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <line x1="12" y1="22" x2="12" y2="7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

const CopyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const DollarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const ReferralDashboard = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isReferrer, setIsReferrer] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [optingIn, setOptingIn] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rewardConfig, setRewardConfig] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);

  // Fetch referral config (rewards and tiers)
  const fetchConfig = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/referral/config');
      if (response.data.success) {
        setRewardConfig(response.data.config);
        setTiers(response.data.tiers || []);
      }
    } catch (error) {
      console.error('Error fetching referral config:', error);
      // Use fallback defaults
      setTiers([
        { id: 'FREE', name: 'Free' },
        { id: 'PRO', name: 'Pro' },
        { id: 'PREMIUM', name: 'Premium' },
        { id: 'ENTERPRISE', name: 'Enterprise' }
      ]);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/referral/merchant/status');
      if (response.data.success) {
        setIsReferrer(response.data.isReferrer);
        setReferrer(response.data.referrer);

        // If already a referrer, fetch dashboard data
        if (response.data.isReferrer) {
          const dashResponse = await axiosInstance.get('/referral/merchant/dashboard');
          if (dashResponse.data.success) {
            setDashboard(dashResponse.data);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching referral status:', error);
      showToast('Failed to load referral data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchPayouts = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/referral/merchant/payouts');
      if (res.data?.success) setPayouts(res.data.payouts || []);
    } catch {
      // Non-fatal — show empty list
    }
  }, []);

  const fetchBankInfo = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/referral/merchant/payouts/bank-info');
      if (res.data?.success) setBankInfo(res.data.bank);
    } catch {
      // Non-fatal — modal will fall back to manual entry
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchStatus();
  }, [fetchConfig, fetchStatus]);

  useEffect(() => {
    if (isReferrer) {
      fetchPayouts();
      fetchBankInfo();
    }
  }, [isReferrer, fetchPayouts, fetchBankInfo]);

  // Payout API bindings — cookie auth via axiosInstance
  const payoutApi = {
    loadBanks: async () => {
      const res = await axiosInstance.get('/referral/merchant/payouts/banks');
      return res.data?.banks || [];
    },
    verifyAccount: async ({ accountNumber, bankCode }) => {
      const res = await axiosInstance.post('/referral/merchant/payouts/verify-account', { accountNumber, bankCode });
      return res.data;
    },
    submit: async (payload) => {
      const res = await axiosInstance.post('/referral/merchant/payouts/request', payload);
      return res.data;
    }
  };

  const handleOptIn = async () => {
    try {
      setOptingIn(true);
      const response = await axiosInstance.post('/referral/merchant/opt-in');
      if (response.data.success) {
        setIsReferrer(true);
        setReferrer(response.data.referrer);
        showToast('Welcome to the referral program!', 'success');
        // Fetch dashboard data
        fetchStatus();
      }
    } catch (error) {
      console.error('Error opting in:', error);
      showToast(error.response?.data?.error || 'Failed to join program', 'error');
    } finally {
      setOptingIn(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    showToast('Copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const getReferralLink = () => {
    const domain = getPlatformDomainName();
    const protocol = domain.includes('localhost') ? 'http' : 'https';
    return `${protocol}://${domain}/signup?ref=${referrer?.referralCode}`;
  };

  const shareReferral = () => {
    const link = getReferralLink();
    const text = `Join Revstar and start selling online! Use my referral code: ${referrer?.referralCode}`;

    if (navigator.share) {
      navigator.share({
        title: 'Join Revstar',
        text: text,
        url: link
      });
    } else {
      copyToClipboard(link);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="referral-dashboard">
        <div className="referral-loading">
          <div className="referral-spinner" />
          <p>Loading referral program...</p>
        </div>
      </div>
    );
  }

  // Not yet a referrer - show opt-in screen
  if (!isReferrer) {
    return (
      <div className="referral-dashboard">
        <div className="referral-optin">
          <div className="optin-icon">
            <GiftIcon />
          </div>
          <h1>Join the Referral Program</h1>
          <p className="optin-description">
            Earn rewards by referring other merchants to Revstar!
            Get paid when they sign up and upgrade to paid plans.
          </p>

          <div className="reward-tiers">
            <h3>Reward Structure</h3>
            <div className="reward-grid">
              {(tiers.length > 0 ? tiers : [
                { id: 'FREE', name: 'Free' },
                { id: 'PRO', name: 'Pro' },
                { id: 'PREMIUM', name: 'Premium' },
                { id: 'ENTERPRISE', name: 'Enterprise' }
              ]).map(tier => {
                const merchantRewards = rewardConfig?.merchantRewardsByTier || {};
                const rewardAmount = merchantRewards[tier.id] || 0;
                return (
                  <div key={tier.id} className="reward-item">
                    <span className="tier">{tier.name} {tier.id === 'FREE' ? 'Signup' : 'Upgrade'}</span>
                    <span className="amount">{formatCurrency(rewardAmount)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            className="optin-button"
            onClick={handleOptIn}
            disabled={optingIn}
          >
            {optingIn ? 'Joining...' : 'Join Referral Program'}
          </button>

          <p className="optin-terms">
            By joining, you agree to our referral program terms and conditions.
          </p>
        </div>
      </div>
    );
  }

  // Already a referrer - show dashboard
  return (
    <div className="referral-dashboard">
      <div className="referral-header">
        <div className="header-content">
          <h1>Referral Program</h1>
          <p>Share your code and earn rewards for each merchant that joins!</p>
        </div>
      </div>

      {/* User Profile Section */}
      <div className="referral-profile-card">
        <div className="profile-avatar">{(dashboard?.referrer?.name || 'M')[0].toUpperCase()}</div>
        <div className="profile-info">
          <span className="profile-name">{dashboard?.referrer?.name || 'Referrer'}</span>
          <span className="profile-email">{dashboard?.referrer?.email}</span>
        </div>
        <span className={`profile-status profile-status--${referrer?.status?.toLowerCase()}`}>
          {referrer?.status || 'ACTIVE'}
        </span>
      </div>

      {/* Referral Code Section */}
      <div className="referral-code-section">
        <div className="code-card">
          <label>Your Referral Code</label>
          <div className="code-display">
            <span className="code">{referrer?.referralCode}</span>
            <button
              className="copy-btn"
              onClick={() => copyToClipboard(referrer?.referralCode)}
              title="Copy code"
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </button>
          </div>
        </div>

        <div className="link-card">
          <label>Your Referral Link</label>
          <div className="link-display">
            <input
              type="text"
              value={getReferralLink()}
              readOnly
              className="link-input"
            />
            <button
              className="copy-btn"
              onClick={() => copyToClipboard(getReferralLink())}
              title="Copy link"
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </button>
            <button
              className="share-btn"
              onClick={shareReferral}
              title="Share"
            >
              <ShareIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon users">
            <UsersIcon />
          </div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.referrer?.totalReferrals || 0}</span>
            <span className="stat-label">Total Referrals</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon verified">
            <CheckIcon />
          </div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.referrer?.verifiedReferrals || 0}</span>
            <span className="stat-label">Verified</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon paid">
            <TrendingUpIcon />
          </div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.referrer?.paidReferrals || 0}</span>
            <span className="stat-label">Upgraded to Paid</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon earnings">
            <DollarIcon />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(dashboard?.referrer?.totalRewardsEarned)}</span>
            <span className="stat-label">Total Earned</span>
          </div>
        </div>
      </div>

      {/* Earnings Summary */}
      <div className="earnings-section">
        <h2>Earnings Summary</h2>
        <div className="earnings-cards">
          <div className="earnings-card pending">
            <span className="earnings-label">Pending Rewards</span>
            <span className="earnings-value">{formatCurrency(dashboard?.referrer?.pendingRewards)}</span>
          </div>
          <div className="earnings-card paid">
            <span className="earnings-label">Paid Out</span>
            <span className="earnings-value">{formatCurrency(dashboard?.referrer?.paidRewards)}</span>
          </div>
        </div>
      </div>

      {/* Wallet + Payout */}
      <div className="rd-wallet">
        <div className="rd-wallet-info">
          <span className="rd-wallet-label">Available to withdraw</span>
          <span className="rd-wallet-value">{formatCurrency(dashboard?.referrer?.walletBalance)}</span>
        </div>
        <button
          type="button"
          className="rd-wallet-btn"
          onClick={() => {
            const min = Number(dashboard?.referrer?.minimumPayout) || 2000;
            const bal = Number(dashboard?.referrer?.walletBalance) || 0;
            if (bal < min) {
              showToast(`You need at least ${formatCurrency(min)} to request a payout`, 'info');
              return;
            }
            setPayoutModalOpen(true);
          }}
        >
          Request Payout
        </button>
      </div>

      {/* Payout history */}
      {payouts.length > 0 && (
        <div className="payout-history">
          <h3 className="payout-history__title">Payout requests</h3>
          <div className="payout-history__list">
            {payouts.slice(0, 6).map((p) => (
              <div key={p.id} className="payout-history__item">
                <div className="payout-history__main">
                  <div className="payout-history__amount">{formatCurrency(p.amount)}</div>
                  <div className="payout-history__meta">
                    {p.bankName} · ••{String(p.accountNumber || '').slice(-4)} · {new Date(p.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <span className={`payout-history__badge payout-history__badge--${(p.status || '').toLowerCase()}`}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Referrals */}
      {dashboard?.referrals?.length > 0 && (
        <div className="referrals-section">
          <h2>Recent Referrals</h2>
          <div className="referrals-list">
            {dashboard.referrals.map((referral) => (
              <div key={referral.id} className="referral-item">
                <div className="referral-info">
                  <span className="referral-name">{referral.referredName}</span>
                  <span className="referral-date">
                    {new Date(referral.signedUpAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="referral-status">
                  <span className={`status-badge ${referral.status.toLowerCase()}`}>
                    {referral.status}
                  </span>
                  <span className="referral-tier">{referral.currentTier}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Rewards */}
      {dashboard?.recentRewards?.length > 0 && (
        <div className="rewards-section">
          <h2>Recent Rewards</h2>
          <div className="rewards-list">
            {dashboard.recentRewards.map((reward) => (
              <div key={reward.id} className="reward-item">
                <div className="reward-info">
                  <span className="reward-type">{reward.rewardType}</span>
                  <span className="reward-event">{reward.triggerEvent}</span>
                </div>
                <div className="reward-amount">
                  <span className={`status-badge ${reward.status.toLowerCase()}`}>
                    {reward.status}
                  </span>
                  <span className="amount">{formatCurrency(reward.rewardAmount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payout Modal */}
      <PayoutModal
        open={payoutModalOpen}
        onClose={() => setPayoutModalOpen(false)}
        walletBalance={Number(dashboard?.referrer?.walletBalance) || 0}
        minimumPayout={Number(dashboard?.referrer?.minimumPayout) || 2000}
        defaultBank={bankInfo}
        loadBanks={payoutApi.loadBanks}
        verifyAccount={payoutApi.verifyAccount}
        submit={payoutApi.submit}
        showToast={(msg, type) => showToast(msg, type)}
        onSuccess={() => {
          fetchStatus();
          fetchPayouts();
        }}
      />
    </div>
  );
};

export default ReferralDashboard;
